// ==================== FILE: src/components/FlowBuilder/properties/ConditionProperties.jsx ====================
import React, { useEffect, useState } from 'react';
import {
    VStack,
    FormControl,
    FormLabel,
    Input,
    Select,
    Button,
    HStack,
    Box,
    Text,
    RadioGroup,
    Radio,
    Stack,
    Checkbox,
    Divider,
    Alert,
    AlertIcon,
    AlertDescription,
    useToast
} from '@chakra-ui/react';
import { FiAlertCircle } from 'react-icons/fi';
import {  } from '../utils/predefinedEvents';
import API from '../../../helpers/axiosInstance';
import { getApiHeaders } from '../../../utils/DefaultFunction';

export default function ConditionProperties({
    nodeConfig,
    setNodeConfig,
    updateNodeData,
    setSelectedNode,
    selectedNode,
    edges,
}) {
    const [events, setEvents] = useState([]);
    const [eventAttributes, setEventAttributes] = useState({});
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [loadingAttributes, setLoadingAttributes] = useState({});
    const [selectedEventName, setSelectedEventName] = useState('');
    const toast = useToast();
   
    // Fetch events on component mount
    useEffect(() => {
        fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch attributes when event is selected
    useEffect(() => {
        if (selectedEventName) {
            fetchEventAttributes(selectedEventName, selectedNode?.id);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEventName]);

    const checkApiCredentials = () => {
        // Add your API credential check logic here
        return true;
    };



    const fetchEvents = async () => {
        if (!checkApiCredentials()) return;

        setLoadingEvents(true);
        try {
            const response = await API.post(
                `${process.env.REACT_APP_API_URL}/fetch-unique-events`,
                {},
                { headers: getApiHeaders() }
            );

            if (response.data.status && response.data.data && response.data.data.events) {
                setEvents(response.data.data.events);

            } else {
                toast({
                    title: "Error",
                    description: response.data.message || "Failed to fetch events",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error fetching events:', error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch events";
            toast({
                title: "Error",
                description: errorMessage,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoadingEvents(false);
        }
    };

    const fetchEventAttributes = async (eventName, conditionId) => {
        if (!eventName || !checkApiCredentials()) return;

        // Check if we already have attributes for this event
        if (eventAttributes[eventName]) {
            return;
        }

        setLoadingAttributes(prev => ({ ...prev, [conditionId]: true }));

        try {
            const response = await API.post(
                `${process.env.REACT_APP_API_URL}/fetch-unique-attributes`,
                { event: eventName },
                { headers: getApiHeaders() }
            );

            if (response.data.status) {
                setEventAttributes(prev => ({
                    ...prev,
                    [eventName]: response.data.data.attributes
                }));

            } else {
                toast({
                    title: "Error",
                    description: response.data.message || "Failed to fetch event attributes",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error fetching event attributes:', error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch event attributes";
            toast({
                title: "Error",
                description: errorMessage,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoadingAttributes(prev => ({ ...prev, [conditionId]: false }));
        }
    };

    const handleEventChange = (eventDisplayName) => {
        setNodeConfig({ ...nodeConfig, eventSource: eventDisplayName });

        // Find the event name for API call
        const selectedEvent = events.find(e => e.display_name === eventDisplayName);
        if (selectedEvent) {
            setSelectedEventName(selectedEvent.name);
        }
    };

    // Get attributes for currently selected event
    const currentAttributes = selectedEventName ? (eventAttributes[selectedEventName] || []) : [];
    const isLoadingCurrentAttributes = loadingAttributes[selectedNode?.id] || false;


    return (
        <VStack spacing={4} align="stretch">
            {/* Condition Name */}
            <FormControl>
                <FormLabel fontSize="sm">Condition Name</FormLabel>
                <Input
                    size="sm"
                    value={nodeConfig.label}
                    onChange={(e) => setNodeConfig({ ...nodeConfig, label: e.target.value })}
                    placeholder="e.g., Purchase Completed?"
                />
            </FormControl>

            {/* Configuration Summary Preview */}
            {nodeConfig.eventSource && (
                <Box p={3} bgGradient="linear(to-br, orange.50, yellow.50)" border="2px" borderColor="orange.200" borderRadius="md">
                    <HStack spacing={2} mb={2}>
                        <FiAlertCircle color="orange" />
                        <Text fontSize="xs" fontWeight="medium" color="orange.900">
                            Condition Summary
                        </Text>
                    </HStack>
                    <VStack align="start" spacing={1.5}>
                        <HStack>
                            <Text fontSize="xs" fontWeight="medium" color="orange.900">
                                Checking:
                            </Text>
                            <Box as="span" fontSize="xs" fontFamily="mono" bg="white" px={1.5} py={0.5} borderRadius="sm">
                                {nodeConfig.eventSource}
                            </Box>
                        </HStack>

                        {nodeConfig.enableAttributeMatching && nodeConfig.matchAttribute && (
                            <Box p={2} bg="white" borderRadius="md" border="1px" borderColor="orange.200" w="full">
                                <Text fontSize="xs" color="orange.800" mb={1}>
                                    Attribute Match:
                                </Text>
                                <Box fontFamily="mono" fontSize="xs" wordBreak="break-words">
                                    <Text as="span" color="orange.600" fontWeight="medium">
                                        {nodeConfig.eventSource}.{nodeConfig.matchAttribute}
                                    </Text>
                                    {' '}
                                    <Text as="span" color="gray.500">
                                        {nodeConfig.comparisonType === 'equals' ? '=' :
                                            nodeConfig.comparisonType === 'greater_than' ? '>' :
                                                nodeConfig.comparisonType === 'less_than' ? '<' :
                                                    nodeConfig.comparisonType}
                                    </Text>
                                    {' '}
                                    <Text as="span" color="purple.600" fontWeight="medium">
                                        {nodeConfig.referenceType === 'message_variable'
                                            ? `{{${nodeConfig.messageVariable || 'variable'}}}`
                                            : nodeConfig.referenceType === 'previous_event'
                                                ? nodeConfig.previousEventRef || 'previous.value'
                                                : `"${nodeConfig.staticValue || 'value'}"`}
                                    </Text>
                                </Box>
                            </Box>
                        )}

                        {nodeConfig.conditionLogic === 'within_time' && (
                            <Text fontSize="xs" color="blue.700" fontWeight="medium">
                                ⏱ Within {nodeConfig.timeWindowValue} {nodeConfig.timeWindowUnit}
                            </Text>
                        )}

                        {nodeConfig.conditionLogic === 'count_of_events' && (
                            <Text fontSize="xs" color="blue.700" fontWeight="medium">
                                🔢 Event Count {nodeConfig.countOperator} {nodeConfig.countValue}
                            </Text>
                        )}
                    </VStack>
                </Box>
            )}

            <Divider />

            {/* Event Source */}
            <FormControl>
                <FormLabel fontSize="sm">Event Source</FormLabel>
                <Select
                    size="sm"
                    value={nodeConfig.eventSource}
                    onChange={(e) => setNodeConfig({ ...nodeConfig, eventSource: e.target.value })}
                    placeholder="Select event"
                >
                    {events.map((event) => (
                        <option key={event.name} value={event.display_name}>
                            {event.display_name}
                        </option>
                    ))}
                </Select>
            </FormControl>

            {/* Condition Logic */}
            <FormControl>
                <FormLabel fontSize="sm">Condition Logic</FormLabel>
                <RadioGroup
                    value={nodeConfig.conditionLogic}
                    onChange={(value) => setNodeConfig({ ...nodeConfig, conditionLogic: value })}
                >
                    <Stack spacing={2}>
                        <Radio value="occurred" size="sm">
                            Event Occurred
                        </Radio>
                        {/* <Radio value="not_occurred" size="sm">
                            Event Not Occurred
                        </Radio> */}
                        <Radio value="within_time" size="sm">
                            Event Occurred Within Time Window
                        </Radio>
                        <Radio value="attribute_check" size="sm">
                            Event Attribute Check
                        </Radio>
                        <Radio value="count_of_events" size="sm">
                            Count of Events
                        </Radio>
                    </Stack>
                </RadioGroup>
            </FormControl>

            {/* Time Window Configuration */}
            {nodeConfig.conditionLogic === 'within_time' && (
                <Box p={3} bg="blue.50" border="1px" borderColor="blue.200" borderRadius="md">
                    <FormLabel fontSize="xs" mb={2}>Time Window</FormLabel>
                    <HStack>
                        <Input
                            size="sm"
                            type="number"
                            min="1"
                            value={nodeConfig.timeWindowValue}
                            onChange={(e) => setNodeConfig({ ...nodeConfig, timeWindowValue: e.target.value })}
                            placeholder="1"
                        />
                        <Select
                            size="sm"
                            value={nodeConfig.timeWindowUnit}
                            onChange={(e) => setNodeConfig({ ...nodeConfig, timeWindowUnit: e.target.value })}
                        >
                            <option value="hours">Hours</option>
                            <option value="days">Days</option>
                            <option value="weeks">Weeks</option>
                        </Select>
                    </HStack>
                    <Text fontSize="xs" color="gray.600" mt={2}>
                        Check if event occurred within the specified time after the previous step
                    </Text>
                </Box>
            )}

            {/* Count of Events Configuration */}
            {nodeConfig.conditionLogic === 'count_of_events' && (
                <Box p={3} bg="green.50" border="1px" borderColor="green.200" borderRadius="md">
                    <FormLabel fontSize="xs" mb={2}>Event Count Configuration</FormLabel>
                    <VStack spacing={3}>
                        <Select
                            size="sm"
                            value={nodeConfig.countOperator || 'equals'}
                            onChange={(e) => setNodeConfig({ ...nodeConfig, countOperator: e.target.value })}
                            placeholder="Select operator"
                        >
                            <option value="equals">Equals</option>
                            <option value="not_equals">Not Equals</option>
                            <option value="greater_than">Greater Than</option>
                            <option value="less_than">Less Than</option>
                            <option value="greater_than_or_equal">Greater Than or Equal</option>
                            <option value="less_than_or_equal">Less Than or Equal</option>
                        </Select>
                        <Input
                            size="sm"
                            type="number"
                            min="0"
                            value={nodeConfig.countValue || ''}
                            onChange={(e) => setNodeConfig({ ...nodeConfig, countValue: e.target.value })}
                            placeholder="e.g., 222"
                        />
                    </VStack>
                    <Text fontSize="xs" color="gray.600" mt={2}>
                        Check if the event count matches the specified condition
                    </Text>
                </Box>
            )}

            {/* Attribute Check Configuration */}
            {nodeConfig.conditionLogic === 'attribute_check' && (
                <Box p={3} bg="purple.50" border="1px" borderColor="purple.200" borderRadius="md">
                    <FormLabel fontSize="xs" mb={2}>Attribute Configuration</FormLabel>
                    <VStack spacing={3}>
                        <Input
                            size="sm"
                            value={nodeConfig.attributeName}
                            onChange={(e) => setNodeConfig({ ...nodeConfig, attributeName: e.target.value })}
                            placeholder="e.g., cart_value"
                        />
                        <Select
                            size="sm"
                            value={nodeConfig.operator}
                            onChange={(e) => setNodeConfig({ ...nodeConfig, operator: e.target.value })}
                            placeholder="Operator"
                        >
                            <option value="equals">Equals</option>
                            <option value="not_equals">Not Equals</option>
                            <option value="greater_than">Greater Than</option>
                            <option value="less_than">Less Than</option>
                            <option value="contains">Contains</option>
                        </Select>
                        <Input
                            size="sm"
                            value={nodeConfig.attributeValue}
                            onChange={(e) => setNodeConfig({ ...nodeConfig, attributeValue: e.target.value })}
                            placeholder="Value"
                        />
                    </VStack>
                </Box>
            )}

            <Divider />

            {/* ==================== ATTRIBUTE MATCHING (ADVANCED) ==================== */}
            {/* <Accordion allowToggle>
                <AccordionItem border="1px" borderColor="gray.200" borderRadius="md">
                    <AccordionButton
                        _hover={{ bg: 'gray.50' }}
                        onClick={() => setNodeConfig({
                            ...nodeConfig,
                            enableAttributeMatching: !nodeConfig.enableAttributeMatching
                        })}
                    >
                        <HStack flex="1" textAlign="left" spacing={2}>
                            <FiDatabase color="orange" />
                            <Text fontSize="sm" fontWeight="medium">
                                Attribute Matching (Advanced)
                            </Text>
                        </HStack>
                        <AccordionIcon />
                    </AccordionButton>

                    <AccordionPanel pb={4} pt={3}>
                        <VStack spacing={3} align="stretch">
                           
                            <FormControl>
                                <FormLabel fontSize="xs">Event to Check</FormLabel>
                                <Select
                                    size="sm"
                                    value={nodeConfig.eventSource}
                                    onChange={(e) => handleEventChange(e.target.value)}
                                    placeholder={loadingEvents ? "Loading events..." : "Select event"}
                                    isDisabled={loadingEvents}
                                >
                                    {events.map((event) => (
                                        <option key={event.name} value={event.display_name}>
                                            {event.display_name}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>

                          
                            <FormControl>
                                <FormLabel fontSize="xs">Attribute to Check</FormLabel>
                                <Select
                                    size="sm"
                                    value={nodeConfig.matchAttribute}
                                    onChange={(e) => setNodeConfig({ ...nodeConfig, matchAttribute: e.target.value })}
                                    placeholder={isLoadingCurrentAttributes ? "Loading attributes..." : "Select attribute from event"}

                                >
                                    {currentAttributes.map((attr) => (
                                        <option key={attr.name} value={attr.name}>
                                            {attr.name} ({attr.type})
                                        </option>
                                    ))}
                                </Select>
                                {nodeConfig.matchAttribute && (
                                    <Text fontSize="xs" color="gray.600" mt={1}>
                                        Will check: <Text as="span" fontFamily="mono" color="blue.700">
                                            {nodeConfig.eventSource}.{nodeConfig.matchAttribute}
                                        </Text>
                                    </Text>
                                )}
                                {isLoadingCurrentAttributes && (
                                    <HStack mt={2} spacing={2}>
                                        <Spinner size="xs" />
                                        <Text fontSize="xs" color="gray.600">Loading attributes...</Text>
                                    </HStack>
                                )}
                            </FormControl>

                        
                            <FormControl>
                                <FormLabel fontSize="xs">Comparison Type</FormLabel>
                                <Select
                                    size="sm"
                                    value={nodeConfig.comparisonType}
                                    onChange={(e) => setNodeConfig({ ...nodeConfig, comparisonType: e.target.value })}
                                    placeholder="Select comparison"
                                >
                                    <option value="equals">Equals (=)</option>
                                    <option value="contains">Contains</option>
                                    <option value="starts_with">Starts With</option>
                                    <option value="greater_than">Greater Than (&gt;)</option>
                                    <option value="less_than">Less Than (&lt;)</option>
                                </Select>
                            </FormControl>

                           
                            <FormControl>
                                <FormLabel fontSize="xs">Compare Against</FormLabel>
                                <Select
                                    size="sm"
                                    value={nodeConfig.referenceType}
                                    onChange={(e) => setNodeConfig({ ...nodeConfig, referenceType: e.target.value })}
                                    placeholder="What to compare against"
                                >
                                    <option disabled style={{ fontWeight: 'bold', fontSize: '11px' }}>
                                        📊 Previous Event
                                    </option>
                                    <option value="previous_event">Attribute from Previous Event</option>
                                    <option disabled style={{ fontWeight: 'bold', fontSize: '11px' }}>
                                        📧 Message Variable
                                    </option>
                                    <option value="message_variable">Variable Sent in Message</option>
                                    <option disabled style={{ fontWeight: 'bold', fontSize: '11px' }}>
                                        🔤 Fixed Value
                                    </option>
                                    <option value="static">Static/Fixed Value</option>
                                </Select>
                            </FormControl>

                          
                            {nodeConfig.referenceType === 'previous_event' && (
                                <VStack spacing={2} align="stretch">
                                    <FormControl>
                                        <FormLabel fontSize="xs">Previous Event</FormLabel>
                                        <Select
                                            size="sm"
                                            value={nodeConfig.previousEventRef?.split('.')[0] || ''}
                                            onChange={(event) => {
                                                const currentAttr = nodeConfig.previousEventRef?.split('.')[1] || '';
                                                setNodeConfig({
                                                    ...nodeConfig,
                                                    previousEventRef: currentAttr ? `${event.target.value}.${currentAttr}` : event.target.value,
                                                });
                                            }}
                                            placeholder="Select event"
                                        >
                                            {events.map((event) => (
                                                <option key={event.name} value={event.display_name}>
                                                    {event.display_name}
                                                </option>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel fontSize="xs">Attribute from Previous Event</FormLabel>

                                        <Select
                                            size="sm"
                                            value={nodeConfig.matchAttribute}
                                            onChange={(e) => setNodeConfig({ ...nodeConfig, matchAttribute: e.target.value })}
                                            placeholder={isLoadingCurrentAttributes ? "Loading attributes..." : "Select attribute from event"}

                                        >
                                            {currentAttributes.map((attr) => (
                                                <option key={attr.name} value={attr.name}>
                                                    {attr.name} ({attr.type})
                                                </option>
                                            ))}
                                        </Select>
                                        
                                    </FormControl>
                                </VStack>
                            )}

                       
                            {nodeConfig.referenceType === 'message_variable' && (
                                <FormControl>
                                    <FormLabel fontSize="xs">Message Variable Name</FormLabel>
                                    <Input
                                        size="sm"
                                        value={nodeConfig.messageVariable}
                                        onChange={(e) => setNodeConfig({ ...nodeConfig, messageVariable: e.target.value })}
                                        placeholder="e.g., promo_code"
                                    />
                                </FormControl>
                            )}

                       
                            {nodeConfig.referenceType === 'static' && (
                                <FormControl>
                                    <FormLabel fontSize="xs">Static/Fixed Value</FormLabel>
                                    <Input
                                        size="sm"
                                        value={nodeConfig.staticValue}
                                        onChange={(e) => setNodeConfig({ ...nodeConfig, staticValue: e.target.value })}
                                        placeholder="e.g., WELCOME20"
                                    />
                                </FormControl>
                            )}

                     
                            <Box p={2} bg="yellow.50" border="1px" borderColor="yellow.200" borderRadius="md">
                                <Checkbox
                                    size="sm"
                                    isChecked={nodeConfig.attributeFallbackEnabled}
                                    onChange={(e) =>
                                        setNodeConfig({ ...nodeConfig, attributeFallbackEnabled: e.target.checked })
                                    }
                                >
                                    <VStack align="start" spacing={0.5}>
                                        <Text fontSize="xs" fontWeight="medium">
                                            Attribute Missing → No Path
                                        </Text>
                                        <Text fontSize="xs" color="gray.600">
                                            If attribute is missing, continue to No Path
                                        </Text>
                                    </VStack>
                                </Checkbox>
                            </Box>
                        </VStack>
                    </AccordionPanel>
                </AccordionItem>
            </Accordion>

            <Divider /> */}

            {/* ==================== BRANCH OUTCOMES ==================== */}
            <FormControl>
                <FormLabel fontSize="xs" mb={2} color="gray.600">
                    Branch Outcomes
                </FormLabel>
                <Box border="1px" borderColor="gray.200" borderRadius="md" p={3} bg="gray.50">
                    <HStack spacing={3}>
                        <Box flex={1}>
                            <HStack mb={1.5}>
                                <Box w={2} h={2} rounded="full" bg="green.500" />
                                <Text fontSize="xs" fontWeight="medium">
                                    Yes Path
                                </Text>
                            </HStack>
                            <Input
                                size="sm"
                                value={nodeConfig.yesLabel}
                                onChange={(e) => setNodeConfig({ ...nodeConfig, yesLabel: e.target.value })}
                                placeholder="Yes"
                            />
                        </Box>
                        <Box flex={1}>
                            <HStack mb={1.5}>
                                <Box w={2} h={2} rounded="full" bg="red.500" />
                                <Text fontSize="xs" fontWeight="medium">
                                    No Path
                                </Text>
                            </HStack>
                            <Input
                                size="sm"
                                value={nodeConfig.noLabel}
                                onChange={(e) => setNodeConfig({ ...nodeConfig, noLabel: e.target.value })}
                                placeholder="No"
                            />
                        </Box>
                    </HStack>

                    {/* Connection Warnings */}
                    {selectedNode && !edges.some((e) => e.source === selectedNode.id && e.sourceHandle === 'yes') && (
                        <Alert status="warning" mt={2} borderRadius="md" py={2}>
                            <AlertIcon boxSize="12px" />
                            <AlertDescription fontSize="xs">Yes path is not connected</AlertDescription>
                        </Alert>
                    )}
                    {selectedNode && !edges.some((e) => e.source === selectedNode.id && e.sourceHandle === 'no') && (
                        <Alert status="warning" mt={2} borderRadius="md" py={2}>
                            <AlertIcon boxSize="12px" />
                            <AlertDescription fontSize="xs">No path is not connected</AlertDescription>
                        </Alert>
                    )}
                </Box>
            </FormControl>

            {/* Fallback Behavior */}
            <Box p={3} bg="gray.50" borderRadius="md">
                <Checkbox
                    size="sm"
                    isChecked={nodeConfig.fallbackEnabled}
                    onChange={(e) => setNodeConfig({ ...nodeConfig, fallbackEnabled: e.target.checked })}
                >
                    <VStack align="start" spacing={0.5}>
                        <Text fontSize="xs" fontWeight="medium">
                            Enable Fallback Behavior
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                            If event data unavailable → continue No Path
                        </Text>
                    </VStack>
                </Checkbox>
            </Box>

            {/* Execution Flow Preview */}
            {nodeConfig.enableAttributeMatching && nodeConfig.matchAttribute && nodeConfig.eventSource && (
                <Box p={3} bg="blue.50" border="1px" borderColor="blue.200" borderRadius="md">
                    <HStack spacing={2} mb={2}>
                        <FiAlertCircle color="blue" />
                        <Text fontSize="xs" fontWeight="medium" color="blue.900">
                            Execution Flow
                        </Text>
                    </HStack>
                    <VStack align="start" spacing={1} fontSize="xs">
                        <Text color="blue.700" fontWeight="medium" mb={1}>
                            When this condition evaluates:
                        </Text>
                        <VStack align="start" spacing={1} color="blue.600" pl={2}>
                            <HStack spacing={1}>
                                <Text>1.</Text>
                                <Text>
                                    Capture <Text as="span" fontFamily="mono" bg="white" px={1} borderRadius="sm">{nodeConfig.eventSource}</Text> event
                                </Text>
                            </HStack>
                            <HStack spacing={1}>
                                <Text>2.</Text>
                                <Text>
                                    Extract <Text as="span" fontFamily="mono" bg="white" px={1} borderRadius="sm">{nodeConfig.matchAttribute}</Text> attribute from event
                                </Text>
                            </HStack>
                            <HStack spacing={1} flexWrap="wrap">
                                <Text>3.</Text>
                                <Text>
                                    Compare using <Text as="span" fontFamily="mono" bg="white" px={1} borderRadius="sm">{nodeConfig.comparisonType}</Text> operator against{' '}
                                    {nodeConfig.referenceType === 'message_variable' ? (
                                        <Text as="span" fontFamily="mono" bg="white" px={1} borderRadius="sm">
                                            {`{{${nodeConfig.messageVariable || 'variable'}}}`}
                                        </Text>
                                    ) : nodeConfig.referenceType === 'previous_event' ? (
                                        <Text as="span" fontFamily="mono" bg="white" px={1} borderRadius="sm">
                                            {nodeConfig.previousEventRef || 'previous.value'}
                                        </Text>
                                    ) : (
                                        <Text as="span" fontFamily="mono" bg="white" px={1} borderRadius="sm">
                                            "{nodeConfig.staticValue || 'value'}"
                                        </Text>
                                    )}
                                </Text>
                            </HStack>
                            <HStack spacing={1}>
                                <Text>4.</Text>
                                <Text>
                                    If match → <Text as="span" color="green.600" fontWeight="medium">{nodeConfig.yesLabel}</Text> path, else → <Text as="span" color="red.600" fontWeight="medium">{nodeConfig.noLabel}</Text> path
                                </Text>
                            </HStack>
                        </VStack>
                    </VStack>
                </Box>
            )}

            {/* Execution Flow Preview for Count of Events */}
            {nodeConfig.conditionLogic === 'count_of_events' && nodeConfig.eventSource && nodeConfig.countValue && (
                <Box p={3} bg="green.50" border="1px" borderColor="green.200" borderRadius="md">
                    <HStack spacing={2} mb={2}>
                        <FiAlertCircle color="green" />
                        <Text fontSize="xs" fontWeight="medium" color="green.900">
                            Count Logic Flow
                        </Text>
                    </HStack>
                    <VStack align="start" spacing={1} fontSize="xs">
                        <Text color="green.700" fontWeight="medium" mb={1}>
                            When this condition evaluates:
                        </Text>
                        <VStack align="start" spacing={1} color="green.600" pl={2}>
                            <HStack spacing={1}>
                                <Text>1.</Text>
                                <Text>
                                    Count occurrences of <Text as="span" fontFamily="mono" bg="white" px={1} borderRadius="sm">{nodeConfig.eventSource}</Text> event
                                </Text>
                            </HStack>
                            <HStack spacing={1}>
                                <Text>2.</Text>
                                <Text>
                                    Check if count <Text as="span" fontFamily="mono" bg="white" px={1} borderRadius="sm">{nodeConfig.countOperator}</Text>{' '}
                                    <Text as="span" fontFamily="mono" bg="white" px={1} borderRadius="sm">{nodeConfig.countValue}</Text>
                                </Text>
                            </HStack>
                            <HStack spacing={1}>
                                <Text>3.</Text>
                                <Text>
                                    If condition met → <Text as="span" color="green.600" fontWeight="medium">{nodeConfig.yesLabel}</Text> path, else → <Text as="span" color="red.600" fontWeight="medium">{nodeConfig.noLabel}</Text> path
                                </Text>
                            </HStack>
                        </VStack>
                    </VStack>
                </Box>
            )}

            {/* Action Buttons */}
            <HStack>
                <Button size="sm" variant="outline" onClick={() => setSelectedNode(null)}>
                    Cancel
                </Button>
                <Button size="sm" colorScheme="orange" onClick={updateNodeData} flex={1}>
                    Update Node
                </Button>
            </HStack>
        </VStack>
    );
}